import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { useHubSpotForm } from "@src/hooks/useHubSpotForm";
import {
    AboutContactSettings,
    getTopLevelLinks,
    MenuLink,
    SocialSettings,
} from "./menuUtils";
import { PanelContent } from "./PanelContent";
import { SocialFooter } from "./SocialFooter";
import { MenuMaskClose } from "./MenuMaskClose";
import "./SideHeaderMenu.scss";

export const SideHeaderMenu = ({ settings }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [mobilePanel, setMobilePanel] = useState<MenuLink | null>(null);
    const iconRef = useRef(null);
    const menuButtonRef = useRef(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const scrollLockRef = useRef(0);

    const isMobile = useMobileBreakpoint("1024px");
    const menuLinks = getTopLevelLinks(settings);
    const section = settings?.settings?.section || {};
    const globals = settings?.settings?.globals || {};

    const social: SocialSettings = {
        instagramUrl: section.social_instagram_url,
        linkedinUrl: section.social_linkedin_url,
        youtubeUrl: section.social_youtube_url,
        facebookUrl: section.social_facebook_url,
    };

    const aboutContact: AboutContactSettings = {
        companyName: section.about_company_name,
        address: section.about_address,
        phone: section.about_phone,
        phoneLabel: section.about_phone_label,
        email: section.about_email,
        emailLabel: section.about_email_label,
    };

    const logoUrl =
        globals.logo ||
        "https://mindarc.myshopify.com/cdn/shop/files/MindArc_Main_Logo.png?v=1735509074&width=1200&width=640&format=webp";

    const { openForm } = useHubSpotForm({
        portalId: globals.hubspot?.portal_id,
        formId: globals.hubspot?.form_id,
        region: globals.hubspot?.form_region,
    });

    const activePanel = menuLinks[activeIndex] || menuLinks[0];

    const checkHeaderVisibility = () => {
        const headerActions = document.querySelector(".header-toolbar");
        if (headerActions) {
            const rect = headerActions.getBoundingClientRect();
            setIsVisible(rect.bottom < 0);
        }
    };

    const onOpenMenu = (e) => {
        if (e.detail.message === "header-menu") {
            setIsVisible(true);
            setIsOpen(true);
        }
    };

    const closeMenu = () => {
        setIsOpen(false);
        setMobilePanel(null);
    };

    const toggleMenu = () => {
        if (isOpen) {
            closeMenu();
        } else {
            setIsOpen(true);
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", checkHeaderVisibility);
        window.addEventListener("onOpenMenu", onOpenMenu);
        checkHeaderVisibility();

        return () => {
            window.removeEventListener("scroll", checkHeaderVisibility);
            window.removeEventListener("onOpenMenu", onOpenMenu);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMenu();
            }
        };

        scrollLockRef.current = window.scrollY;
        document.body.style.overflow = "hidden";
        document.body.style.width = "100%";

        if (isMobile) {
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollLockRef.current}px`;
            document.documentElement.style.overflow = "hidden";
        }

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("pagehide", closeMenu);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("pagehide", closeMenu);

            document.body.style.overflow = "";
            document.body.style.width = "";
            document.body.style.position = "";
            document.body.style.top = "";
            document.documentElement.style.overflow = "";

            if (isMobile) {
                window.scrollTo(0, scrollLockRef.current);
            }
        };
    }, [isOpen, isMobile]);

    useEffect(() => {
        if (!isOpen) {
            setMobilePanel(null);
            setActiveIndex(0);
        }
    }, [isOpen]);

    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = 0;
        }
    }, [mobilePanel, isOpen]);

    const handleContactClick = () => {
        openForm();
    };

    const menuOverlay = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`fixed inset-0 flex flex-col z-40 side-header-menu__backdrop${
                        isMobile ? " side-header-menu__backdrop--mobile-fullscreen" : " m-[18px] rounded-[4px]"
                    }`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Site navigation"
                >
                    <div
                        className={`menu side-header-menu${
                            isMobile ? " side-header-menu--mobile" : ""
                        }`}
                    >
                        <div className="side-header-menu__overlay-inner">
                            <div
                                className={`side-header-menu__header${
                                    isMobile && mobilePanel
                                        ? " side-header-menu__header--mobile-drill"
                                        : ""
                                }`}
                            >
                                <div className="side-header-menu__header-row">
                                    <div className="side-header-menu__logo">
                                        <img
                                            src={logoUrl}
                                            alt="MindArc"
                                            className="w-full h-auto object-contain invert"
                                        />
                                    </div>
                                    {!isMobile && (
                                        <button
                                            type="button"
                                            className="side-header-menu__contact-btn"
                                            onClick={handleContactClick}
                                        >
                                            <span>Contact us</span>
                                            <span
                                                className="icon-right-arrow-alt side-header-menu__contact-icon"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    )}
                                </div>
                                {!isMobile && (
                                    <div className="side-header-menu__title">Menu</div>
                                )}
                            </div>

                            <div
                                ref={bodyRef}
                                className="side-header-menu__body"
                            >
                                {isMobile ? (
                                    mobilePanel ? (
                                        <div className="side-header-menu__mobile-nav">
                                            <button
                                                type="button"
                                                onClick={() => setMobilePanel(null)}
                                                className="side-header-menu__mobile-back"
                                            >
                                                ←&nbsp;&nbsp;&nbsp;Menu
                                            </button>
                                            <p className="side-header-menu__mobile-title">
                                                {mobilePanel.title}
                                            </p>
                                            <div
                                                className="side-header-menu__mobile-divider"
                                                aria-hidden="true"
                                            />
                                            <PanelContent
                                                panel={mobilePanel}
                                                aboutContact={aboutContact}
                                                layout="mobile"
                                            />
                                        </div>
                                    ) : (
                                        <nav
                                            className="side-header-menu__mobile-l1"
                                            aria-label="Main menu"
                                        >
                                            {menuLinks.map((link) => (
                                                <button
                                                    type="button"
                                                    key={link.handle || link.title}
                                                    className="side-header-menu__mobile-l1-item"
                                                    onClick={() => {
                                                        if (link.links && link.links.length > 0) {
                                                            setMobilePanel(link);
                                                        } else if (link.url) {
                                                            window.location.href = link.url;
                                                        }
                                                    }}
                                                >
                                                    <span>{link.title}</span>
                                                    {link.links && link.links.length > 0 && (
                                                        <span
                                                            className="side-header-menu__mobile-l1-arrow"
                                                            aria-hidden="true"
                                                        >
                                                            →
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </nav>
                                    )
                                ) : (
                                    <div className="side-header-menu__mega">
                                        <nav className="side-header-menu__l1" aria-label="Main menu">
                                            {menuLinks.map((link, index) => {
                                                const isActive = index === activeIndex;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={link.handle || link.title}
                                                        className={`side-header-menu__l1-item ${
                                                            isActive
                                                                ? "side-header-menu__l1-item--active"
                                                                : "side-header-menu__l1-item--inactive"
                                                        }`}
                                                        onMouseEnter={() => setActiveIndex(index)}
                                                        onFocus={() => setActiveIndex(index)}
                                                        onClick={() => setActiveIndex(index)}
                                                    >
                                                        {link.title}
                                                    </button>
                                                );
                                            })}
                                        </nav>
                                        <div className="side-header-menu__divider" aria-hidden="true" />
                                        <div className="side-header-menu__content">
                                            <PanelContent
                                                panel={activePanel}
                                                aboutContact={aboutContact}
                                                layout="desktop"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="side-header-menu__footer">
                                {isMobile && (
                                    <button
                                        type="button"
                                        className="side-header-menu__contact-btn"
                                        onClick={handleContactClick}
                                    >
                                        <span>Contact us</span>
                                        <span
                                            className="icon-right-arrow-alt side-header-menu__contact-icon"
                                            aria-hidden="true"
                                        />
                                    </button>
                                )}
                                <SocialFooter
                                    social={social}
                                    variant={isMobile ? "mobile" : "desktop"}
                                    onLinkClick={isMobile ? closeMenu : undefined}
                                />
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {!isMobile && isOpen ? (
                <MenuMaskClose onClick={closeMenu} />
            ) : (
                <div
                    ref={menuButtonRef}
                    className={`fixed z-50 transition-all duration-500 ${
                        isMobile
                            ? `mobile-menu-button top-0 right-0`
                            : `desktop-menu-button top-1/2 -translate-y-1/2 right-0 -mr-[220px] rounded-full ${
                                  isVisible || isOpen ? "translate-x-0" : "translate-x-full"
                              }`
                    } outside-ring`}
                    style={{
                        width: `${isMobile ? "64px" : "300px"}`,
                        height: `${isMobile ? "64px" : "170px"}`,
                        border: `${isMobile ? "none" : "2px solid"}`,
                        borderColor: "#D6CFF8",
                        backgroundColor: `${isOpen ? "#FBF8F4" : "rgba(38, 38, 38, 0.60)"}`,
                        backdropFilter: "blur(6.9px)",
                    }}
                >
                    <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                        className={`absolute ${
                            isMobile
                                ? "top-[55%] -translate-y-1/2 left-[50%] -translate-x-1/2 z-[1]"
                                : "top-1/2 -translate-y-1/2 left-[5%]"
                        } p-3 text-[32px] ${isOpen ? "text-white" : "text-black"}`}
                        onClick={toggleMenu}
                    >
                        {!isOpen && (
                            <span
                                ref={iconRef}
                                className={`open-icon icon-menu pointer-events-none ${
                                    isMobile ? "text-black" : "text-[#F1E8DE]"
                                }`}
                            />
                        )}
                        {isOpen && <span className="open-icon icon-X text-black" />}
                    </button>
                </div>
            )}

            {typeof document !== "undefined"
                ? createPortal(menuOverlay, document.body)
                : menuOverlay}
        </>
    );
};

export default SideHeaderMenu;
